import { useEffect, useRef, useState } from 'react';
import type { FormikErrors } from 'formik';
import { isDirectoryEmail, type ClientEmailType, type EmailEntryType, type EmailEntryWriteType } from '@econtractor/types';
import { useTranslation } from 'react-i18next';
import * as api from 'api/apiEmailsDir';
import { getClientEmails } from 'api/apiClients';
import { useAuth } from 'context/AuthContext';
import { useToast } from 'context/ToastContext';
import { useModal } from 'context/ModalContext';
import { getButtonsNoYesDanger } from 'context/helpers/helperModalContext';
import type { EmailPickerRow, OffEmailsDirProps } from './types';

const useEmailsDir = ({ show, onHide, onSelect }: OffEmailsDirProps) => {
    const { t } = useTranslation('EmailsDir');
    const { auth } = useAuth();
    const { showToast } = useToast();
    const { showDlg } = useModal();
    const [clients, setClients] = useState<ClientEmailType[] | null>(null);
    const [entries, setEntries] = useState<EmailEntryType[] | null>(null);
    const [clientError, setClientError] = useState(false);
    const [entriesError, setEntriesError] = useState(false);
    const [activeTab, setActiveTab] = useState('clients');
    const [search, setSearch] = useState({ clients: '', others: '' });
    const [selected, setSelected] = useState<Record<string, string>>({});
    const [editing, setEditing] = useState<EmailEntryType | 'new' | null>(null);
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    const session = useRef(0);

    useEffect(() => {
        const token = ++session.current;
        if (!show) return;
        setClients(null);
        setEntries(null);
        setClientError(false);
        setEntriesError(false);
        setSearch({ clients: '', others: '' });
        setSelected({});
        setEditing(null);
        setActiveTab('clients');
        void getClientEmails().then((rows) => {
            if (session.current !== token) return;
            setClients(rows || []);
            setClientError(rows === null);
        });
        void api.getList().then((rows) => {
            if (session.current !== token) return;
            setEntries(rows || []);
            setEntriesError(rows === null);
        });
        return () => { session.current = token + 1; };
    }, [show]);

    const refresh = async (token: number) => {
        const rows = await api.getList();
        if (session.current !== token) return;
        setEntriesError(rows === null);
        if (rows) {
            setEntries(rows);
            setSelected((prev) => {
                const next = { ...prev };
                Object.keys(next).filter((key) => key.startsWith('other:')).forEach((key) => {
                    const row = rows.find((entry) => `other:${entry.ID}` === key);
                    if (row) next[key] = row.email;
                    else delete next[key];
                });
                return next;
            });
        }
    };

    const validate = (values: EmailEntryWriteType): FormikErrors<EmailEntryWriteType> => {
        const errors: FormikErrors<EmailEntryWriteType> = {};
        if (!values.name.trim() || values.name.trim().length > 250) errors.name = t('invalidName');
        if (!isDirectoryEmail(values.email.trim())) errors.email = t('invalidEmail');
        return errors;
    };

    const save = async (values: EmailEntryWriteType) => {
        if (busyRef.current || editing === null) return;
        const token = session.current;
        busyRef.current = true;
        setBusy(true);
        try {
            const error = await api.save(editing === 'new' ? null : editing.ID, {
                name: values.name.trim(),
                email: values.email.trim().toLowerCase(),
                isPublic: values.isPublic,
            });
            if (session.current !== token) return;
            if (error) {
                showToast(t(error === 'DUPLICATE' ? 'duplicate' : 'error'), 'danger');
                return;
            }
            if (editing !== 'new') {
                const key = `other:${editing.ID}`;
                setEntries((prev) => prev?.map((entry) => entry.ID === editing.ID ? { ...entry, name: values.name.trim(), email: values.email.trim().toLowerCase(), isPublic: values.isPublic } : entry) ?? null);
                setSelected((prev) => prev[key] ? { ...prev, [key]: values.email.trim().toLowerCase() } : prev);
            }
            setEditing(null);
            await refresh(token);
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
    };

    const remove = (entry: EmailEntryType) => {
        const buttons = getButtonsNoYesDanger(t);
        const token = session.current;
        buttons[1].handleClick = async () => {
            showDlg(false);
            if (busyRef.current || session.current !== token) return;
            busyRef.current = true;
            setBusy(true);
            try {
                const ok = await api.remove(entry.ID);
                if (session.current !== token) return;
                if (!ok) showToast(t('error'), 'danger');
                else {
                    setEntries((prev) => prev?.filter((row) => row.ID !== entry.ID) ?? null);
                    if (editing !== 'new' && editing?.ID === entry.ID) setEditing(null);
                    setSelected((prev) => {
                        const next = { ...prev };
                        delete next[`other:${entry.ID}`];
                        return next;
                    });
                }
                await refresh(token);
            } finally {
                busyRef.current = false;
                setBusy(false);
            }
        };
        showDlg({ type: 'confirm', text: t('confirmDelete', { name: entry.name }), buttons });
    };

    const toggle = (row: EmailPickerRow) => setSelected((prev) => {
        const next = { ...prev };
        if (next[row.key]) delete next[row.key];
        else next[row.key] = row.email;
        return next;
    });
    const clientRows: EmailPickerRow[] = (clients || []).map((row) => ({ key: `client:${row.clientId}:${row.email}`, name: row.clientName, email: row.email, clientId: row.clientId, clientPhone: row.clientPhone }));
    const otherRows: EmailPickerRow[] = (entries || []).map((entry) => ({ key: `other:${entry.ID}`, name: entry.name, email: entry.email, entry }));
    const filter = (rows: EmailPickerRow[], query: string) => rows.filter((row) => `${row.name} ${row.email} ${row.entry?.authorName || ''}`.toLowerCase().includes(query.trim().toLowerCase()));
    const close = () => { if (!busyRef.current) onHide(); };
    const choose = () => {
        if (busyRef.current || !Object.keys(selected).length) return;
        onSelect(Array.from(new Set(Object.values(selected).map((email) => email.toLowerCase()))));
        onHide();
    };
    const chooseClient = (row: EmailPickerRow) => {
        if (busyRef.current) return;
        onSelect(Array.from(new Set([...Object.values(selected), row.email].map((email) => email.toLowerCase()))));
        onHide();
    };

    return {
        activeTab, setActiveTab, search, setSearch, selected, toggle, editing, setEditing,
        busy, validate, save, remove, close, choose, chooseClient, clientError, entriesError,
        clientsLoading: clients === null, entriesLoading: entries === null,
        clientRows: filter(clientRows, search.clients), otherRows: filter(otherRows, search.others),
        userId: Number(auth?.user?.ID),
    };
};

export default useEmailsDir;
